/* eslint-disable max-lines -- the create adapter plus the composer ports both chat and Home bind; this root refuses a second module. */

import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import { tauriAudioLevelsAtom } from '@/atoms/tauriAudioLevelsAtom';
import { getBarHeights } from '@/components/mic-selector/optimized/getBarHeights';
import { ImageFileCard } from '@/components/ui/attachments/ImageFileCard';
import { ComposerDictateSlot } from '@/components/ui/composer-dictate-slot';
import { LinkedContextBadge } from '@/components/ui/linked-context-badge';
import { useDictationStore } from '@/features/dictation/dictationBubble/store/useDictationStore';
import { useIsChatObjectRefsEnabled } from '@/features/flags';
import { IS_CHAT_ATTACHMENTS_ENABLED } from '@/features/flags/legacy-flags';
import { ProjectsComposerPromptEditor } from '@/features/projects/components/ProjectsComposerPromptEditor';
import { useDesktopTargetSelection } from '@/features/rpa/desktop-target/useDesktopTargetSelection';
import { VoiceAssistantTrigger } from '@/features/voice-assistant';
import { useReferenceableLibraryDocuments } from '@/features/workflows/components/useWorkflowContextDocumentUpload';
import { useRunAnyWorkflow } from '@/features/workflows/hooks';
import { useAudioLevelsSubscription } from '@/hooks/desktop/useAudioLevelsSubscription';
import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import { getPreviewUrl, useChatFileUpload } from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { useChatPaste } from '@/hooks/useChatPaste';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { SM } from '@/hooks/useWindowDimensions';
import { trackWorkHomeComposerCta } from '@/models/analytics/features/trackWorkHomeComposerAnalytics';
import {
  cancelNativeDictation,
  shouldUseNativeAudio,
  stopNativeDictation,
} from '@/tauri/dictation';
import { rpaBridge } from '@/tauri/rpa';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import {
  Composer,
  ComposerDictationBar,
  composerUploadedAttachments,
  DesktopTargetSelector,
  Heidi2EvidenceTourAddMenuStep,
  mergePickedFiles,
  PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
  useAvailableWorkflowSkillsWithWorkflows,
  useLinkableContextItems,
  useStagedAttachments,
  useWorkComposerSourcesSlot,
  useWorkCreateSubmit,
  type ComposerAddMenuBind,
  type ComposerAttachments,
  type ComposerDictateSlotBind,
  type ComposerDictationBind,
  type ComposerDictationPorts,
  type ComposerGlow,
  type ComposerHost,
  type ComposerPromptEditorBind,
  type ComposerProps,
  type ProjectsComposerPromptEditorHandle,
  type UseLinkableContextItemsArgs,
  type WorkComposerSessionAppearance,
  type WorkCreateCommand,
  type WorkCreateSubmit,
  type WorkHomeSceneProps,
} from '@heidi/agents';
import {
  DEFAULT_WORK_CHAT_APPROVAL_MODE,
  linkedContextItemLabel,
  linkedContextItemSubtitle,
  type LinkedContextItem,
  type WorkSurface,
} from '@heidi/agents/contracts';
import {
  composerDraftStorageKey,
  composerLinkedItemsAtom,
  PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES,
  projectChatComposerMessages,
  PROJECTS_COMPOSER_MAX_STAGED_FILES,
  setComposerLinkedItemAtom,
  useChatAttachmentUploadAdapter,
  useComposerDraftPersistence,
  useWorkChatApprovalModePreference,
  useWorkRunSpeedPreference,
  type ChatAttachmentUploadPorts,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerSkillReference,
} from '@heidi/agents/data';
import { useAudioAnalyzer } from '@heidi/capture';
import { toast } from '@heidi/design-system/toast';
import type { NavigatePort } from '@heidi/runtime/shell';
import { normalizePastedFile } from '@heidi/utils';
import type { Editor } from '@tiptap/react';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';
import { LinkContextButton } from './LinkContextButton';
import { type ProjectsComposerPromptEditorProps } from './ProjectsComposerPromptEditor';

export type ProjectsComposerSessionAppearance = WorkComposerSessionAppearance;

type WorkCreateComposerHost = Extract<
  ComposerHost,
  { kind: 'explore' } | { kind: 'routine' }
>;

export interface ProjectsLandingComposerProps {
  host?: WorkCreateComposerHost;
  onSubmit?: (command: WorkCreateCommand) => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  className?: string;
  showRunOptions?: boolean;
  showSpeed?: boolean;
  placeholder?: string;
  appearance?: 'default' | 'flat';
  showVoiceTrigger?: boolean;
  glow?: ComposerGlow;
  disabledMessage?: string;
  sendDisabled?: boolean;
  composerDockLeading?: ReactNode;
  toolbarSourcesSlot?: ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const EXPLORE_COMPOSER_HOST: WorkCreateComposerHost = { kind: 'explore' };

const noop = (): void => undefined;

interface ComposerPromptEditorPortOptions {
  includeCreateWorkflow?: boolean;
  includeRecordAction?: boolean;
  slashSectionOrder?: ProjectsComposerPromptEditorProps['slashSectionOrder'];
  /** Where the editor's own slash picker opens against the composer card. */
  pickerPlacement?: 'above' | 'below';
  /** Submit the editor's live draft rather than the composer's settled one. */
  submitsEditorDraft?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  prefilledValue?: string;
  placeholderPreview?: string | null;
  analyticsSurface?: WorkSurface;
  className?: string;
  editorClassName?: string;
  onLinkContext?: (item: LinkedContextItem | null) => void;
  onUnhandledKeyDown?: (event: KeyboardEvent) => boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onClipboardPaste?: (event: ClipboardEvent) => boolean;
  onEditorChange?: (editor: Editor | null) => void;
  onPickerOpenChange?: (open: boolean) => void;
  patientScope?: UseLinkableContextItemsArgs['patientScope'];
}

const composerPromptEditorPort = (options: ComposerPromptEditorPortOptions = {}) =>
  function ComposerPromptEditorPort(bind: ComposerPromptEditorBind): ReactNode {
    return (
      <ProjectsComposerPromptEditor
        ref={bind.editorRef as Ref<ProjectsComposerPromptEditorHandle>}
        pickerAnchorRef={bind.anchorRef}
        pickerPlacement={options.pickerPlacement}
        availableSkills={bind.availableSkills}
        autoFocus={options.autoFocus ?? bind.autoFocus}
        placeholder={bind.placeholder}
        placeholderPreview={options.placeholderPreview ?? bind.placeholderPreview}
        placeholderOverlay={bind.placeholderOverlay}
        disabled={options.disabled ?? bind.disabled}
        prefilledValue={options.prefilledValue ?? bind.prefilledValue}
        prefilledSkill={bind.prefilledSkill}
        onDraftChange={bind.onDraftChange}
        onSubmitRequest={(draft) =>
          bind.onSubmitRequest(options.submitsEditorDraft ? draft : undefined)
        }
        onShortcutSend={bind.onShortcutSend}
        onUnhandledKeyDown={options.onUnhandledKeyDown}
        onLinkContext={(item) => (options.onLinkContext ?? bind.onLinkContext)(item)}
        onOpenArtifact={(artifact) => bind.onOpenArtifact(artifact)}
        objectTokens={bind.objectTokens}
        onFocus={options.onFocus}
        onBlur={options.onBlur}
        onClipboardPaste={
          options.onClipboardPaste ??
          (bind.onClipboardPaste
            ? (event) => {
                bind.onClipboardPaste?.(event);
                return event.defaultPrevented;
              }
            : undefined)
        }
        onEditorChange={(editor) => {
          options.onEditorChange?.(editor);
          bind.onEditorChange(editor);
        }}
        onPickerOpenChange={options.onPickerOpenChange}
        includeCreateWorkflow={options.includeCreateWorkflow}
        includeRecordAction={options.includeRecordAction}
        slashSectionOrder={options.slashSectionOrder}
        analyticsSurface={options.analyticsSurface ?? bind.analyticsSurface}
        patientScope={options.patientScope}
        className={options.className ?? bind.className}
        editorClassName={options.editorClassName ?? bind.editorClassName}
        testId={bind.testId}
      />
    );
  };

interface ComposerAddMenuModeRowSpotlight {
  open: boolean;
  wrap: (row: ReactNode) => ReactNode;
  onModeRowToggle?: () => void;
}

interface ComposerAddMenuPortOptions {
  onSet?: (item: LinkedContextItem | null) => void;
  onAfterPick?: () => void;
  skillsMenu?: ComposerAddMenuBind['skillsMenu'];
  speedMenu?: ComposerAddMenuBind['speedMenu'];
  modeMenu?: ComposerAddMenuBind['modeMenu'];
  disabled?: boolean;
  hideLinkedContextPickers?: boolean;
  hidePatientPicker?: boolean;
  patientScope?: UseLinkableContextItemsArgs['patientScope'];
  analyticsSurface?: WorkSurface;
  /**
   * Work-home Evidence tour over the Evidence-only row. Resolved here so the
   * Chat page does not import the tour or the on-prem build flag.
   */
  evidenceTour?: boolean;
  renderShortcutsFlyout?: (actions: {
    onSend: (query: string) => void;
    close: () => void;
  }) => ReactNode;
}

const composerAddMenuPort = (options: ComposerAddMenuPortOptions = {}) =>
  function ComposerAddMenuPort(bind: ComposerAddMenuBind): ReactNode {
    const hostFlyout = options.renderShortcutsFlyout;
    const onShortcutSend = bind.onShortcutSend;
    const button = (modeRowSpotlight?: ComposerAddMenuModeRowSpotlight) => (
      <LinkContextButton
        items={bind.items}
        onSet={options.onSet ?? bind.onSet}
        onPickObject={bind.onPickObject}
        onAfterPick={options.onAfterPick ?? bind.onAfterPick}
        onAttachFiles={bind.onAttachFiles}
        skillsMenu={options.skillsMenu ?? bind.skillsMenu}
        speedMenu={options.speedMenu ?? bind.speedMenu}
        modeMenu={options.modeMenu ?? bind.modeMenu}
        modeRowSpotlight={modeRowSpotlight}
        hideLinkedContextPickers={options.hideLinkedContextPickers}
        hidePatientPicker={options.hidePatientPicker}
        patientScope={options.patientScope}
        disabled={options.disabled ?? bind.disabled}
        analyticsSurface={options.analyticsSurface ?? bind.analyticsSurface}
        showSourcesInAddMenu={bind.showSourcesInAddMenu}
        renderShortcutsFlyout={
          hostFlyout && onShortcutSend
            ? (close) => hostFlyout({ onSend: onShortcutSend, close })
            : undefined
        }
      />
    );

    if (!options.evidenceTour || isOnPrem) return button();

    return (
      <Heidi2EvidenceTourAddMenuStep>
        {(spotlight) => button(spotlight)}
      </Heidi2EvidenceTourAddMenuStep>
    );
  };

const composerDictateSlotPort = (options: { disabled?: boolean } = {}) =>
  function ComposerDictateSlotPort(bind: ComposerDictateSlotBind): ReactNode {
    return (
      <ComposerDictateSlot
        editor={bind.editor}
        getEditor={bind.getEditor}
        disabled={options.disabled ?? bind.disabled}
        variant="ghost"
        size="md"
        buttonClassName={bind.buttonClassName}
        onDictateClick={bind.onDictateClick}
      />
    );
  };

const useReadyToRequestMic = () => useAtomValue(readyToRequestMicPermissionsAtom);
// Read imperatively, once per frame, so a ~30 FPS level stream never re-renders.
const readNativeAudioLevels = () => getDefaultStore().get(tauriAudioLevelsAtom);
// Nothing writes the atom above unless a consumer holds this listener, so the
// reader and its holder cross together.
const useNativeAudioLevelsSubscription = (active: boolean) =>
  useAudioLevelsSubscription(active);

/**
 * The app half of the package's live-dictation row. Bound once at module scope
 * — every member is a hook or a module function, so the object identity never
 * changes and the waveform's rAF loop is never torn down by a fresh object.
 *
 * `useAudioAnalyzer` crosses as a port rather than being imported inside the
 * package: `@heidi/capture`'s door constructs an `AudioContext` at module scope
 * and the `@heidi/agents` door is eagerly evaluated, so importing it there
 * would boot an audio graph on every route that touches the door.
 */
const COMPOSER_DICTATION_PORTS: ComposerDictationPorts = {
  useDictationStore,
  // `isTauri` (the desktop host) and `shouldUseNativeAudio` (native capture)
  // are different predicates; the row needs both.
  isNativeHost: isTauri,
  stopNativeDictation,
  cancelNativeDictation,
  shouldUseNativeAudio,
  useReadyToRequestMic,
  useAudioAnalyzer,
  readNativeAudioLevels,
  useAudioLevelsSubscription: useNativeAudioLevelsSubscription,
  getBarHeights,
};

const composerDictationBarPort = () =>
  function ComposerDictationBarPort(bind: ComposerDictationBind): ReactNode {
    return (
      <ComposerDictationBar
        editor={bind.editor}
        getEditor={bind.getEditor}
        onActiveChange={bind.onActiveChange}
        ports={COMPOSER_DICTATION_PORTS}
      />
    );
  };

const CREATE_PROMPT_EDITOR = composerPromptEditorPort({
  includeRecordAction: true,
  pickerPlacement: 'below',
});
const CREATE_ADD_MENU = composerAddMenuPort();
const CREATE_DICTATE_SLOT = composerDictateSlotPort();
const CREATE_DICTATION_BAR = composerDictationBarPort();

/**
 * The app's half of a routine launch from the composer: the routine run
 * mutation and the route push into the run it started. The launch lock and the
 * create routing are the package's.
 */
const useRoutineLaunchPorts = () => {
  const router = useRouter();
  const runAnyWorkflow = useRunAnyWorkflow();
  const onNavigate = useCallback<NavigatePort>(
    async (href) => {
      await router.push(href);
    },
    [router]
  );
  return { runRoutine: runAnyWorkflow.mutate, onNavigate };
};

/** Chat's attachment upload stack, which the Work-home upload adapter wraps. */
const chatAttachmentUploadPorts: ChatAttachmentUploadPorts = {
  useUploadAttachment: useMutateUploadChatAttachment,
  useDeleteAttachment: useMutateDeleteChatAttachment,
  cancelUpload: cancelChatAttachmentUpload,
};

/**
 * Shared by Discover and Agent Home. Chat attachments pre-upload through
 * `POST /chat/attachments` so a later Evidence redirect can carry the ids.
 * With the flag off, files stay staged in the browser.
 */
const useLandingComposerAttachments = (): ComposerAttachments => {
  const intl = useIntl();
  const preUploadEnabled = IS_CHAT_ATTACHMENTS_ENABLED();
  const uploadAdapter = useChatAttachmentUploadAdapter(chatAttachmentUploadPorts);
  const {
    files: preUploadedFiles,
    addFiles: addPreUploadedFiles,
    removeFile: removePreUploadedFile,
    retryUpload: retryPreUploadedFile,
  } = useChatFileUpload({
    adapter: uploadAdapter,
    enableUpload: preUploadEnabled,
    formatErrorMessage: (errorCode) => formatChatUploadErrorMessage(intl, errorCode),
  });
  const stagedAttachments = useStagedAttachments({
    mergePickedFiles,
    accept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
  });
  const { items: uploadedItems, readyToSend: uploadsReadyToSend } = useMemo(
    () => composerUploadedAttachments(preUploadedFiles),
    [preUploadedFiles]
  );
  const uploadedAttachments = useMemo<ComposerAttachments>(
    () => ({
      items: uploadedItems,
      attach: (files) => {
        const existing = preUploadedFiles.map((file) => file.file);
        const { next, error } = mergePickedFiles(existing, files, {
          maxCount: PROJECTS_COMPOSER_MAX_STAGED_FILES,
          oversizedPolicy: 'skip',
        });
        const added = next.slice(existing.length);
        if (error && added.length === 0) toast({ ...error, tone: 'destructive' });
        if (added.length > 0) addPreUploadedFiles(added);
      },
      remove: (id) => removePreUploadedFile(id),
      retry: (id) => retryPreUploadedFile(id),
      readyToSend: uploadsReadyToSend,
      accept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
      renderImageItem: (item, index) => {
        const file = preUploadedFiles[index];
        if (!file) return null;
        return (
          <ImageFileCard
            file={file.file}
            uploadStatus={file.uploadStatus}
            uploadProgress={item.progress}
            previewUrl={getPreviewUrl(file)}
            errorMessage={item.error}
            onDelete={() => removePreUploadedFile(file.uid)}
            onRetry={
              item.status === 'error' ? () => retryPreUploadedFile(file.uid) : undefined
            }
          />
        );
      },
    }),
    [
      addPreUploadedFiles,
      preUploadedFiles,
      removePreUploadedFile,
      retryPreUploadedFile,
      uploadedItems,
      uploadsReadyToSend,
    ]
  );

  return preUploadEnabled ? uploadedAttachments : stagedAttachments;
};

interface WorkHomeComposerWiringBind {
  availableSkills: readonly ProjectsComposerSkillReference[];
  isDesktopBridgeAvailable: boolean;
  onSubmit: WorkCreateSubmit;
  mergePickedFiles: typeof mergePickedFiles;
  attachmentAccept: string;
  normalizeDroppedAttachmentFile: WorkHomeSceneProps['normalizeDroppedAttachmentFile'];
  formatDroppedAttachmentRejection: WorkHomeSceneProps['formatDroppedAttachmentRejection'];
  attachments: ComposerAttachments;
  onClipboardPaste: NonNullable<ComposerProps['onClipboardPaste']>;
}

/**
 * Resolves the app-side composer ports (routine pathway, attachment policy,
 * clipboard paste) outside the Chat-quarantined Home route, which cannot reach
 * these hooks and modules itself.
 */
const WorkHomeComposerWiring = ({
  onCreate,
  isSubmitting = false,
  sendDisabled = false,
  children,
}: {
  onCreate: (command: WorkCreateCommand) => void;
  isSubmitting?: boolean;
  sendDisabled?: boolean;
  children: (bind: WorkHomeComposerWiringBind) => ReactNode;
}): JSX.Element => {
  const isDesktopBridgeAvailable = rpaBridge.isAvailable();
  const workflowSkills = useAvailableWorkflowSkillsWithWorkflows();
  const availableSkills = useMemo(
    () =>
      isDesktopBridgeAvailable
        ? workflowSkills
        : workflowSkills.filter((skill) => skill.kind === 'workflow'),
    [isDesktopBridgeAvailable, workflowSkills]
  );

  // The composer hands its own attach callback in per paste, so the hook routes
  // through a ref rather than closing over one.
  const attachPastedFilesRef =
    useRef<(files: FileList | readonly File[] | null) => void>();
  const { handlePaste } = useChatPaste({
    onFilesDetected: (files) => attachPastedFilesRef.current?.(files),
  });
  const onClipboardPaste = useCallback<WorkHomeComposerWiringBind['onClipboardPaste']>(
    (event, attachFiles) => {
      attachPastedFilesRef.current = attachFiles;
      handlePaste(event);
    },
    [handlePaste]
  );

  const routineLaunch = useRoutineLaunchPorts();
  const onSubmit = useWorkCreateSubmit({
    availableSkills,
    disabled: false,
    sendDisabled,
    isSubmitting,
    onCreate,
    ...routineLaunch,
  });
  const attachments = useLandingComposerAttachments();

  return (
    <>
      {children({
        availableSkills,
        isDesktopBridgeAvailable,
        onSubmit,
        mergePickedFiles,
        attachmentAccept: PROJECT_ATTACHMENTS_ACCEPT_FORMATS,
        normalizeDroppedAttachmentFile: normalizePastedFile,
        formatDroppedAttachmentRejection: (rejections) =>
          mergePickedFiles([], [], { dropRejections: rejections }).error,
        attachments,
        onClipboardPaste,
      })}
    </>
  );
};

const ProjectsLandingComposerView = memo(function ProjectsLandingComposer({
  host = EXPLORE_COMPOSER_HOST,
  onSubmit,
  isSubmitting = false,
  disabled = false,
  className,
  showRunOptions = false,
  showSpeed = false,
  placeholder,
  appearance = 'default',
  showVoiceTrigger = true,
  glow = 'always',
  disabledMessage,
  sendDisabled = false,
  composerDockLeading,
  toolbarSourcesSlot,
  expanded = false,
  onExpandedChange,
}: ProjectsLandingComposerProps) {
  const intl = useIntl();
  const inheritedSourcesSlot = useWorkComposerSourcesSlot();
  const resolvedToolbarSourcesSlot = toolbarSourcesSlot ?? inheritedSourcesSlot;
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !resolvedToolbarSourcesSlot) {
      console.error(
        'ProjectsLandingComposer is layout full and has no Sources toolbar slot. Sources is not shown in the + menu.'
      );
    }
  }, [resolvedToolbarSourcesSlot]);
  const editorRef = useRef<ProjectsComposerPromptEditorHandle | null>(null);
  const [draft, setDraft] = useState<ProjectsComposerPromptDraft>({
    text: '',
    segments: [],
  });
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const chatObjectRefsEnabled = useIsChatObjectRefsEnabled();

  const isDesktopBridgeAvailable = rpaBridge.isAvailable();
  const workflowSkills = useAvailableWorkflowSkillsWithWorkflows();
  const availableSkills = useMemo(
    () =>
      isDesktopBridgeAvailable
        ? workflowSkills
        : workflowSkills.filter((skill) => skill.kind === 'workflow'),
    [isDesktopBridgeAvailable, workflowSkills]
  );

  const { persistedApprovalMode, rememberApprovalMode } =
    useWorkChatApprovalModePreference();
  const { persistedSpeed, rememberSpeed } = useWorkRunSpeedPreference();
  const desktopTarget = useDesktopTargetSelection({ surface: 'landing' });

  const [workflowLaunchingId, setWorkflowLaunchingId] = useState<string | null>(null);

  const workflowBusy = workflowLaunchingId !== null;
  const sendBusy = isSubmitting || workflowBusy;

  const attachments = useLandingComposerAttachments();

  const { sessionItems, patientItems } = useLinkableContextItems({ limit: 50 });
  const libraryDocuments = useReferenceableLibraryDocuments({ limit: 50 });
  const linkableItems = useMemo(
    () => [...sessionItems, ...patientItems, ...libraryDocuments],
    [libraryDocuments, patientItems, sessionItems]
  );
  const { clearPersistedDraft } = useComposerDraftPersistence({
    storageKey: composerDraftStorageKey('landing'),
    enabled: !disabled && !sendBusy,
    draft,
    linkedItems,
    linkableItems,
    editorRef,
    onRestoreLinkedItem: setLinkedItem,
    onDraftRestored: setDraft,
  });

  const attachPastedFilesRef =
    useRef<(files: FileList | readonly File[] | null) => void>();
  const { handlePaste } = useChatPaste({
    onFilesDetected: (files) => attachPastedFilesRef.current?.(files),
    enabled: !disabled && !sendBusy,
  });
  const onClipboardPaste = useCallback<NonNullable<ComposerProps['onClipboardPaste']>>(
    (event, attach) => {
      attachPastedFilesRef.current = attach;
      handlePaste(event);
    },
    [handlePaste]
  );

  const routineLaunch = useRoutineLaunchPorts();
  const handleSubmit = useWorkCreateSubmit({
    availableSkills,
    disabled,
    sendDisabled: sendDisabled || workflowBusy,
    isSubmitting: sendBusy,
    onCreate: onSubmit ?? noop,
    onCreated: clearPersistedDraft,
    onLaunchingChange: setWorkflowLaunchingId,
    ...routineLaunch,
  });

  const isCompactPlaceholder = useMediaQuery(`(max-width: ${SM - 1}px)`);
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage(
      isCompactPlaceholder
        ? projectChatComposerMessages.placeholderCompact
        : projectChatComposerMessages.placeholder,
      PROJECT_CHAT_COMPOSER_PLACEHOLDER_VALUES
    );

  return (
    <>
      <Composer
        host={host}
        layout="full"
        className={className}
        attachments={attachments}
        onSubmit={handleSubmit}
        isSubmitting={sendBusy}
        disabled={disabled}
        disabledMessage={disabledMessage}
        elevated={false}
        glow={glow}
        showRunOptions={showRunOptions}
        showSpeed={showSpeed}
        runSpeed={persistedSpeed}
        onRunSpeedChange={rememberSpeed}
        chatObjectRefsEnabled={chatObjectRefsEnabled}
        placeholder={resolvedPlaceholder}
        draftValue={draft}
        onDraftChange={setDraft}
        onShortcutSubmit={(payload, keptDraft) =>
          handleSubmit(payload, (command) => ({ ...command, keptDraft }))
        }
        promptEditorRef={editorRef}
        availableSkills={availableSkills}
        isDesktopBridgeAvailable={isDesktopBridgeAvailable}
        onClipboardPaste={onClipboardPaste}
        approvalMode={persistedApprovalMode ?? DEFAULT_WORK_CHAT_APPROVAL_MODE}
        onApprovalModeChange={rememberApprovalMode}
        trackCta={trackWorkHomeComposerCta}
        sendDisabled={sendDisabled}
        sendBlocked={workflowBusy}
        persistentSend={!showVoiceTrigger}
        voiceTrigger={showVoiceTrigger ? <VoiceAssistantTrigger /> : undefined}
        composerDockLeading={composerDockLeading}
        toolbarSourcesSlot={resolvedToolbarSourcesSlot}
        promptOverflow={{
          expanded,
          onToggle: onExpandedChange ? () => onExpandedChange(!expanded) : undefined,
        }}
        desktopTargetSlot={
          appearance !== 'flat' && desktopTarget.visible ? (
            <div className="flex w-full items-center ps-2">
              <DesktopTargetSelector
                desktopTarget={desktopTarget}
                disabled={disabled || isSubmitting}
              />
            </div>
          ) : null
        }
        linkedContextBadge={(item, bind) => (
          <LinkedContextBadge
            // The badge's prop union lives app-side (`src/components/ui`) and is
            // deliberately duplicated rather than imported from
            // `@heidi/agents/contracts` — the Chat quarantine forced it out of the
            // Chat tree. A template renders identically to a library document
            // (same FileOutput icon, no subtitle), so map rather than widen it.
            type={item.type === 'template' ? 'library_document' : item.type}
            name={linkedContextItemLabel(item)}
            subtitle={linkedContextItemSubtitle(item)}
            onClick={bind.onOpen}
            onRemove={bind.onRemove}
          />
        )}
        renderPromptEditor={CREATE_PROMPT_EDITOR}
        renderAddMenu={CREATE_ADD_MENU}
        dictateSlot={CREATE_DICTATE_SLOT}
        renderDictation={CREATE_DICTATION_BAR}
      />
    </>
  );
});

// Hung on the existing export so the Work page and the chat adapter can bind
// these ports without a new locked-root symbol or a new Chat-quarantine edge.
export const ProjectsLandingComposer = Object.assign(ProjectsLandingComposerView, {
  PromptEditor: composerPromptEditorPort,
  LinkContext: composerAddMenuPort,
  VoiceTrigger: VoiceAssistantTrigger,
  DictateSlot: composerDictateSlotPort,
  DictationBar: composerDictationBarPort,
  ComposerWiring: WorkHomeComposerWiring,
});
